import { Text, View } from '@react-pdf/renderer'
import React from 'react'

interface MarkdownTextProps {
  children: string
  style?: Record<string, unknown>
  baseFontSize?: number
}

/**
 * Component to render markdown text in PDF documents using @react-pdf/renderer.
 * Supports: headers, bold, italic, lists, line breaks, and paragraphs.
 */
export const MarkdownText: React.FC<MarkdownTextProps> = ({
  children,
  style = {},
  baseFontSize = 11,
}) => {
  if (!children || !children.trim()) {
    return (
      <Text style={{ fontSize: baseFontSize, fontStyle: 'italic' }}>
        No content provided
      </Text>
    )
  }

  const lines = children.split('\n')
  const elements: React.ReactNode[] = []
  let currentParagraph: string[] = []
  let inList = false
  let listItems: string[] = []

  const flushParagraph = () => {
    if (currentParagraph.length > 0) {
      const paragraphText = currentParagraph.join(' ')
      elements.push(
        <View key={`para-${elements.length}`} style={{ marginBottom: 8 }}>
          {parseInlineMarkdown(paragraphText, baseFontSize, style)}
        </View>
      )
      currentParagraph = []
    }
  }

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(
        <View
          key={`list-${elements.length}`}
          style={{ marginBottom: 8, marginLeft: 12 }}>
          {listItems.map((item, idx) => (
            <View key={idx} style={{ flexDirection: 'row', marginBottom: 4 }}>
              <Text style={{ fontSize: baseFontSize, marginRight: 8 }}>•</Text>
              <View style={{ flex: 1 }}>
                {parseInlineMarkdown(item, baseFontSize, style)}
              </View>
            </View>
          ))}
        </View>
      )
      listItems = []
      inList = false
    }
  }

  lines.forEach((line) => {
    const trimmedLine = line.trim()

    if (!trimmedLine) {
      if (inList) {
        flushList()
      } else {
        flushParagraph()
      }
      return
    }

    const headerMatch = trimmedLine.match(/^(#{1,6})\s+(.+)$/)
    if (headerMatch) {
      flushParagraph()
      flushList()
      const level = headerMatch[1].length
      const headerText = headerMatch[2]
      const headerSize = baseFontSize + (7 - level) * 2
      elements.push(
        <Text
          key={`header-${elements.length}`}
          style={{
            fontSize: headerSize,
            fontWeight: 'bold',
            marginBottom: 6,
            marginTop: elements.length > 0 ? 8 : 0,
            ...style,
          }}>
          {headerText}
        </Text>
      )
      return
    }

    const listMatch = trimmedLine.match(/^(?:[-*+]|\d+\.)\s+(.+)$/)
    if (listMatch) {
      if (!inList) {
        flushParagraph()
        inList = true
      }
      listItems.push(listMatch[1])
      return
    }

    if (inList) {
      flushList()
    }
    currentParagraph.push(trimmedLine)
  })

  flushList()
  flushParagraph()

  return <>{elements}</>
}

function parseInlineMarkdown(
  text: string,
  baseFontSize: number,
  baseStyle: Record<string, unknown>
): React.ReactNode {
  const segments: React.ReactNode[] = []
  let key = 0
  let pos = 0

  while (pos < text.length) {
    if (text.substring(pos, pos + 2) === '**') {
      const endPos = text.indexOf('**', pos + 2)
      if (endPos !== -1) {
        const boldText = text.substring(pos + 2, endPos)
        segments.push(
          <Text
            key={key++}
            style={{
              fontSize: baseFontSize,
              fontWeight: 'bold',
              ...baseStyle,
            }}>
            {boldText}
          </Text>
        )
        pos = endPos + 2
        continue
      }
    }

    if (text[pos] === '`') {
      const endPos = text.indexOf('`', pos + 1)
      if (endPos !== -1) {
        const codeText = text.substring(pos + 1, endPos)
        segments.push(
          <Text
            key={key++}
            style={{
              fontSize: baseFontSize - 1,
              fontFamily: 'Courier',
              backgroundColor: '#f5f5f5',
              padding: 2,
              ...baseStyle,
            }}>
            {codeText}
          </Text>
        )
        pos = endPos + 1
        continue
      }
    }

    // Avoid matching italic markers that are part of bold syntax
    if (text[pos] === '*' && text[pos + 1] !== '*' && text[pos - 1] !== '*') {
      const endPos = text.indexOf('*', pos + 1)
      if (endPos !== -1 && text[endPos + 1] !== '*') {
        const italicText = text.substring(pos + 1, endPos)
        segments.push(
          <Text
            key={key++}
            style={{
              fontSize: baseFontSize,
              fontStyle: 'italic',
              ...baseStyle,
            }}>
            {italicText}
          </Text>
        )
        pos = endPos + 1
        continue
      }
    }

    let plainText = ''
    while (pos < text.length && text[pos] !== '*' && text[pos] !== '`') {
      plainText += text[pos]
      pos++
    }

    if (plainText) {
      segments.push(
        <Text key={key++} style={{ fontSize: baseFontSize, ...baseStyle }}>
          {plainText}
        </Text>
      )
    }

    if (plainText === '' && pos < text.length) {
      segments.push(
        <Text key={key++} style={{ fontSize: baseFontSize, ...baseStyle }}>
          {text[pos]}
        </Text>
      )
      pos++
    }
  }

  if (segments.length === 0) {
    return <Text style={{ fontSize: baseFontSize, ...baseStyle }}>{text}</Text>
  }

  return <>{segments}</>
}
